/**
 * The four units a review row actually needs, as one tap-through control.
 *
 * A `<select>` is the right answer in a form, where you set a unit once and
 * move on. It is the wrong one in a review list, where you are correcting a
 * dozen rows in a row: every correction costs opening a picker, finding the
 * value among six, and dismissing it — on a phone, over a sheet that is already
 * scrolled.
 *
 * So: four values, cycled by tapping. Four rather than all six because `ml` and
 * `l` never come off a shelf photo and almost never off a receipt line, and a
 * cycle with two dead stops in it is worse than a cycle without them. The full
 * set is still available in the edit sheet, which is where an unusual unit is
 * worth the trip.
 */

import type { Unit } from "../features/food/types";

import "./UnitToggle.css";

/** In the order someone taps through them, commonest first. */
export const QUICK_UNITS: Unit[] = ["pcs", "g", "kg", "pack"];

interface UnitToggleProps {
  value: Unit;
  disabled?: boolean;
  onChange: (unit: Unit) => void;
}

export function UnitToggle({ value, disabled, onChange }: UnitToggleProps) {
  // A unit outside the cycle — ml or l, off a receipt — is kept and shown
  // rather than silently rewritten, and the first tap moves into the cycle.
  const index = QUICK_UNITS.indexOf(value);

  return (
    <button
      className="unit"
      type="button"
      disabled={disabled}
      // Says what it is *and* what pressing does, because "pcs" alone read out
      // by a screen reader is a label, not a control.
      aria-label={`Unit: ${value}. Change`}
      onClick={() => onChange(QUICK_UNITS[(index + 1) % QUICK_UNITS.length])}
    >
      {value}
    </button>
  );
}
